import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { StripeService } from '../../services/stripeService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiCreditCard, FiDollarSign, FiCheck, FiArrowLeft, FiLoader, FiInfo } = FiIcons

const CreditPurchase = ({ user, onPurchaseComplete, onBack }) => {
  const minCredits = 5
  const maxCredits = 500
  const pricePerCredit = 1
  const [credits, setCredits] = useState(minCredits)
  const [processing, setProcessing] = useState(false)
  const [subscribingPlan, setSubscribingPlan] = useState('')
  const [error, setError] = useState('')
  

  const SUBSCRIPTION_PLANS = [
    { id: 'price_1RxUwGI8bx27Wh5OX4QQ1TMR', name: 'Pro Plan', price: 29, credits: 35, savings: '21% savings', description: '35 credits + Pro Links access' },
    { id: 'price_1RxUyII8bx27Wh5OVnaoGdLq', name: 'Agency Plan', price: 79, credits: 100, savings: '27% savings', description: '100 credits + Pro Links access' }
  ]

  const handleSliderChange = (e) => setCredits(parseInt(e.target.value))

  const handlePurchase = async () => {
    setProcessing(true)
    setError('')
    try {
      const origin = window.location.origin
      const successUrl = `${origin}/#/dashboard?payment=success`
      const cancelUrl = `${origin}/#/dashboard?payment=cancelled`
      const session = await StripeService.createCheckoutSession(
        user.id,
        credits,
        successUrl,
        cancelUrl,
        true
      )

      if (session?.url) {
        window.location.href = session.url
      } else {
        throw new Error('Failed to create checkout session')
      }
    } catch (e) {
      console.error('Purchase error:', e)
      setError('Failed to initialize checkout. Please try again.')
      setProcessing(false)
    }
  }

  const handleSubscribe = async (planId) => {
    try {
      setError('')
      setSubscribingPlan(planId)

      const origin = window.location.origin
      const successUrl = `${origin}/#/dashboard?payment=success`
      const cancelUrl = `${origin}/#/dashboard?payment=cancelled`
      const session = await StripeService.createSubscriptionCheckoutSession(
        user.id,
        planId,
        successUrl,
        cancelUrl,
        true
      )

      if (session?.url) {
        window.location.href = session.url
      } else {
        throw new Error('Failed to create subscription checkout session')
      }
    } catch (e) {
      console.error('Subscription error:', e)
      setError('Failed to initialize subscription checkout. Please try again.')
      setSubscribingPlan('')
    }
  }

  

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="bg-[#D59A4C30] p-4 rounded-full w-16 h-16 mx-auto mb-4">
          <SafeIcon icon={FiCreditCard} className="w-8 h-8 text-[#D59A4C]" />
        </div>
        <h2 className="text-3xl text-gray-900 mb-2 font-bold">
          Purchase Credits
        </h2>
        <p className="text-gray-600">
          <strong>1 credit</strong> can find an average of <strong>200 link opportunities</strong> or generate <strong>400 pieces of outreach content</strong> or scrape <strong>400 email addresses</strong>
        </p>
      </div>

      {/* Credit Slider */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <label className="text-lg font-semibold text-gray-900">Number of Credits</label>
          <div className="text-right">
            <div className="text-3xl font-bold text-[#D59A4C]">{credits}</div>
            <div className="text-sm text-gray-600">credits</div>
          </div>
        </div>

        <input
          type="range"
          min={minCredits}
          max={maxCredits}
          value={credits}
          onChange={handleSliderChange}
          className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          style={{
            background: `linear-gradient(to right, #D59A4C 0%, #D59A4C ${((credits - minCredits) / (maxCredits - minCredits)) * 100}%, #e5e7eb ${((credits - minCredits) / (maxCredits - minCredits)) * 100}%, #e5e7eb 100%)`
          }}
        />

        <div className="flex justify-between text-sm text-gray-600 mt-2">
          <span>{minCredits}</span>
          <span>{maxCredits}</span>
        </div>

        <div className="bg-gray-50 rounded-xl p-6 mb-6 text-center">
          <div className="text-2xl font-bold text-gray-900">{credits} Credits</div>
          <div className="text-2xl font-bold text-[#D59A4C]">${credits * pricePerCredit}</div>
          <div className="text-sm text-gray-600 mt-1">$1.00 per credit</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Purchase Buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={onBack}
          className="flex items-center px-6 py-3 bg-transparent text-gray-700 rounded-lg hover:bg-gray-200 transition-colors no-border"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
          Back
        </button>
        <button
          onClick={handlePurchase}
          disabled={processing}
          className="flex-1 flex items-center justify-center px-6 py-3 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {processing ? (
            <>
              <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-2" />
              Purchase {credits} Credits for ${credits * pricePerCredit}
            </>
          )}
        </button>
      </div>

      {/* Subscription Plans */}
      <div className="mt-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Save by Subscribing!</h3>
        <p className="text-gray-600 mb-6"><strong>Save over 20%</strong> on monthly credits plus get Pro Links directory access.<br/>
          Credits never expire and roll over month to month, cancel anytime.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SUBSCRIPTION_PLANS.map((plan) => (
            <div key={plan.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <span>{plan.name}</span> &nbsp; <span class="px-2 py-1 bg-green-100 text-green-700 rounded-40 text-xs font-medium dark:bg-green-900/30 dark:text-green-400">{plan.savings}</span>
              <div className="text-3xl font-extrabold text-gray-900 mb-1">${plan.price}/mo</div>
              <div className="text-sm text-gray-500 mb-4"><strong>{plan.description}</strong></div>
              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={subscribingPlan === plan.id}
                className="w-full flex items-center justify-center px-6 py-3 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {subscribingPlan === plan.id ? (
                  <>
                    <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : 'Subscribe'}
              </button>
            </div>
          ))}
        </div>
      </div>
      {/* Payment Info */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>Secure payment processing • Credits never expire • No refunds</p>
      </div>
    </motion.div>
  )
}

export default CreditPurchase
